/* eslint-disable max-lines */
import {
    Box,
    Button,
    Checkbox,
    Image,
    Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { parseISO } from 'date-fns';
import { diLogos } from '@acp/core-ui/assets';
import { acceptTermsAndConditions, getPendingTermsAndConditions } from '../service';

const schema = z.object({
    accept: z.boolean().refine((value) => value === true, {
        message: 'You must accept the End User License Agreement to continue',
    }),
});

type FormValues = z.infer<typeof schema>;

function TermsAndConditionsModal() {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: ['terms-and-conditions', 'pending'],
        queryFn: () => getPendingTermsAndConditions(),
    });

    const mutation = useMutation({
        mutationFn: (termsId: number) => acceptTermsAndConditions(termsId),
        onSuccess: () => queryClient.invalidateQueries({
            queryKey: ['terms-and-conditions', 'pending'],
        }),
    });

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            accept: false,
        },
    });

    if (query.isLoading || query.data === undefined) return null;
    if (query.data === null) return null;

    return (
        <Modal size="2xl" isOpen onClose={() => {}} closeOnEsc={false} closeOnOverlayClick={false} isCentered>
            <ModalOverlay backdropFilter="blur(10px) brightness(20%)" />
            <ModalContent>
                <ModalHeader display="flex" flexDir="column" textAlign="center" alignItems="center" pt={6}>
                    <Image src={diLogos.horizontalRGB} w={56} mb={4} />
                    Compliance Portal End User License Agreement (EULA)
                </ModalHeader>
                <ModalBody>
                    <Text textAlign="center" mb={1}>
                        Effective
                        {' '}
                        {parseISO(query.data.effectiveFrom).toLocaleDateString()}
                        {' '}
                        our End User License Agreement (EULA) has been updated.
                    </Text>
                    <Text mb={5} textAlign="center">For any questions please contact your Account Manager.</Text>
                    <Box fontSize="sm" whiteSpace="pre-wrap" h={96} overflowY="scroll" bgColor="charcoal.25" p={1}>
                        {query.data.textContent}
                    </Box>
                    <Controller
                        control={form.control}
                        name="accept"
                        render={({ field, fieldState }) => (
                            <>
                                <Checkbox
                                    isChecked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    mt={5}
                                >
                                    I have read and agree to the End User License Agreement (EULA)
                                </Checkbox>
                                {fieldState.error && <Text fontSize="sm" color="danger.500">{fieldState.error.message}</Text>}
                            </>
                        )}
                    />
                </ModalBody>

                <ModalFooter>
                    <Button
                        onClick={form.handleSubmit(() => mutation.mutate(query.data!.id))}
                        isLoading={mutation.isPending}
                        colorScheme="secondary"
                    >
                        Continue
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export { TermsAndConditionsModal };
